import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { CoreService } from "../../../services/core.service";
import { catchError, take } from "rxjs/operators";
import { of } from "rxjs";
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from "@angular/material/paginator";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Router } from "@angular/router";

@Component({
  selector: "app-social-ai-alerts-table",
  standalone: true,
  imports: [CommonModule, MatPaginatorModule, MatTooltipModule],
  templateUrl: "./social-ai-alerts-table.component.html",
  styleUrl: "./social-ai-alerts-table.component.scss",
})
export class SocialAiAlertsTableComponent implements OnInit, OnChanges {
  @Input() selectedSiteId!: number | null;
  @Input() selectedPlatformId!: number | null;
  @Input() pages: any[] = [];
  @Input() selectedFormat: string | null = null;
  @Input() selectedAlertType: string | null = null;

  companyID: number | null = null;

  tableAlerts: any[] = [];
  isLoading = false;

  totalItems = 0;
  pageSize = 12;
  currentPage = 1;

  alerts: {
    id: number;
    rss_title: string;
    rss_img_url: string;
    Domain: string;
    platform: string;
    alert_text: string;
    alert_type: string;
    format: string;
    posting_queue_id: number;
  }[] = [];

  maxSidebarCards = 3;

  private readonly LATEST_ALERTS_KEY = "latestAlerts";

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(private coreService: CoreService, private router: Router) {}

  ngOnInit(): void {
    this.companyID = this.coreService.user?.companyID || null;
    if (!this.companyID) return;

    this.loadLatestPostAlerts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes["selectedSiteId"] ||
        changes["selectedPlatformId"] ||
        changes["selectedFormat"] ||
        changes["selectedAlertType"]) &&
      this.selectedSiteId &&
      this.selectedPlatformId
    ) {
      this.loadTableAlerts(1);
    }
  }

  loadTableAlerts(page: number = 1): void {
    this.companyID = this.coreService.user?.companyID || null;
    if (!this.companyID) return;

    this.isLoading = true;
    this.currentPage = page;

    const payload: any = {
      company_id: this.companyID,
      contentsite_id: this.selectedSiteId,
      social_platform_id: this.selectedPlatformId,
      page,
    };

    if (this.selectedFormat) {
      payload.format = this.selectedFormat;
    }

    if (this.selectedAlertType) {
      payload.type = this.selectedAlertType;
    }

    this.coreService
      .postData(`generatealert/${this.companyID}`, payload)
      .pipe(
        take(1),
        catchError(() => {
          this.isLoading = false;
          return of({
            alerts: [],
            total: 0,
            per_page: this.pageSize,
            current_page: page,
          });
        })
      )
      .subscribe((res: any) => {
        const alerts = res.alerts || {};

        this.tableAlerts = alerts.data || [];

        this.totalItems = alerts.total ?? 0;
        this.pageSize = alerts.per_page ?? 12;
        this.currentPage = alerts.current_page ?? page;

        // this.setLatestAlerts();

        this.isLoading = false;

        if (this.paginator) {
          this.paginator.length = this.totalItems;
          this.paginator.pageSize = this.pageSize;
          this.paginator.pageIndex = this.currentPage - 1;
        }
      });
  }

  onPageChange(event: PageEvent): void {
    const page = event.pageIndex + 1;
    this.loadTableAlerts(page);
  }

  formatLabel(format: string): string {
    const map: Record<string, string> = {
      fbfirstcomment: "Facebook Photo Post (first comment)",
      fbphotopost: "Facebook Photo Post",
      photolink: "Facebook Link Post",
      fbvideo: "Facebook Reel Post",
      fbstoryvideo: "Facebook Story Video",
      fbstoryphoto: "Facebook Story Photo",

      igphotopost: "Instagram Photo Post",
      igcarousel: "Instagram Carousel",
      igstoryvideo: "Instagram Story Video",
      igstoryphoto: "Instagram Story Photo",
      igtvideo: "Instagram Reel Post",
    };

    return map[format] || format;
  }

  dismissAlert(alertId: number): void {
    if (!this.companyID) return;

    this.coreService
      .postData(`dismisspost/${this.companyID}`, {
        id: alertId,
      })
      .pipe(
        take(1),
        catchError((err) => {
          console.error("Error dismissing alert:", err);
          return of(null);
        })
      )
      .subscribe((res: any) => {
        if (!res) return;

        this.tableAlerts = this.tableAlerts.filter((a) => a.id !== alertId);
        this.totalItems--;

        if (this.paginator) {
          this.paginator.length = this.totalItems;
        }

        if (this.tableAlerts.length === 0 && this.currentPage > 1) {
          this.loadTableAlerts(this.currentPage - 1);
        }

        this.coreService.showSnackbar("Alert dismissed successfully!");
      });
  }

viewAlert(alert: { format: string; posting_queue_id: number }): void {

  this.router.navigate([
    '/preview-post',
    alert.format,
    alert.posting_queue_id,
  ]);
}

  private loadLatestPostAlerts(): void {
    if (!this.companyID) return;

    this.coreService
      .postData(`getlatestpostalerts/${this.companyID}`, {})
      .pipe(
        take(1),
        catchError((err) => {
          console.error("Failed to load latest post alerts", err);
          return of({ alerts: [] });
        })
      )
      .subscribe((res: any) => {
        this.alerts = (res?.alerts || [])
          .slice(0, this.maxSidebarCards)
          .map((a: any) => ({
            id: a.id,
            rss_title: a.rss_title,
            rss_img_url: a.rss_img_url,
            Domain: a.Domain,
            platform: a.platform,
            alert_text: a.alert_text,
            alert_type: a.alert_type,
            format: a.format,
            posting_queue_id: a.posting_queue_id,
          }));

        localStorage.setItem(
          this.LATEST_ALERTS_KEY,
          JSON.stringify(this.alerts)
        );
      });
  }
}
